import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/auth-context";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      // Backend always returns 200 to prevent enumeration; this only fires
      // on a network failure.
      toast.error("Could not reach the server. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-screen bg-[#FBF9F4] flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
        <Link
          to="/login"
          className="inline-flex items-center text-sm text-slate-500 hover:text-[#0A192F] mb-6"
          data-testid="back-to-login"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to login
        </Link>

        {sent ? (
          <div className="space-y-5 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#FFF8E7] border-2 border-[#C5A059] flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-[#C5A059]" />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-[#0A192F]">
              Check your email
            </h1>
            <p className="text-sm text-slate-600 leading-relaxed">
              If <span className="font-medium text-[#0A192F]">{email}</span> is registered,
              we just sent a reset link. It expires in one hour.
            </p>
            <p className="text-xs text-slate-500">
              Didn't get it? Check spam, then try again in a few minutes.
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-semibold text-[#0A192F]">
              Reset your password
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Enter the email on your account. We'll send a link to set a new password.
            </p>
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <label
                  htmlFor="fp-email"
                  className="block text-xs tracking-[0.18em] uppercase font-semibold text-[#0A192F] mb-1.5"
                >
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="fp-email"
                    data-testid="forgot-email-input"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11 rounded-lg border-slate-300"
                  />
                </div>
              </div>
              <Button
                type="submit"
                data-testid="forgot-submit"
                disabled={submitting || !email.trim()}
                className="w-full h-11 rounded-lg bg-[#0A192F] hover:bg-[#112A46] text-white font-semibold"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Send reset link"
                )}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
